// EDV Next 0.11.4 - password recovery UI.
//
// - <ForgotPasswordScreen>: generic ack; never leaks whether the email exists.
// - <SetNewPasswordScreen>: reads token ONCE from URL fragment/query, scrubs
//   the URL immediately, keeps the token only in a React ref during the
//   POST, then clears the ref. The RENDER GUARD is a separate
//   `tokenValid` boolean captured at mount so terminal states (pw-expired /
//   pw-invalid) remain visible after the token ref is nulled.
//
// Token hygiene:
//   * token in a React ref (never in state, never in localStorage)
//   * URL scrubbed with history.replaceState before render commits
//   * error messages redacted before logging
//   * localStorage never touched
import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from './api.js';

const PW_RULES = 'At least 12 characters, mix of uppercase, lowercase, digit, and symbol.';
const PW_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,128}$/;

async function post(path: string, body: unknown): Promise<{ ok: boolean; status: number; data: unknown }> {
  const res = await fetch(`${API_BASE_URL || ''}/api/v1/auth${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', 'x-edv-client': 'web' },
    body: JSON.stringify(body),
  });
  let data: unknown = null;
  try { data = await res.json(); } catch { /* keep null */ }
  return { ok: res.ok, status: res.status, data };
}

export function ForgotPasswordScreen({ onDone }: { onDone(): void }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle'|'sending'|'sent'|'error'>('idle');
  return (
    <form
      data-testid="forgot-password-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setState('sending');
        try {
          await post('/recover', { email });
          setState('sent');
        } catch {
          setState('sent');
        }
      }}
    >
      <h1>Forgot password?</h1>
      <p>Enter your email. If an account exists, you will receive a link.</p>
      <label>
        Email{' '}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
          data-testid="forgot-email"
        />
      </label>
      <button type="submit" disabled={state === 'sending'} data-testid="forgot-submit">
        Send recovery link
      </button>
      {state === 'sent' && (
        <p role="status" aria-live="polite" data-testid="forgot-generic-ack">
          If an account exists for that email, a recovery link has been sent.
        </p>
      )}
      <button type="button" onClick={onDone} data-testid="forgot-back">
        Back to sign in
      </button>
    </form>
  );
}

function readRecoveryTokenAndClearUrl(): { token: string; type: string } | null {
  if (typeof window === 'undefined') return null;
  const fromHash  = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  const fromQuery = new URLSearchParams(window.location.search || '');
  const token =
    fromHash.get('token_hash') ??
    fromHash.get('token') ??
    fromHash.get('access_token') ??
    fromQuery.get('token_hash') ??
    fromQuery.get('token') ??
    fromQuery.get('access_token');
  const type =
    fromHash.get('type') ??
    fromQuery.get('type') ??
    'recovery';
  try {
    window.history.replaceState(null, '', window.location.pathname);
  } catch { /* SSR / restricted */ }
  return token ? { token, type } : null;
}

function redactRecovery(msg: unknown): string {
  const s = typeof msg === 'string' ? msg : (msg instanceof Error ? msg.message : String(msg));
  return s
    .replace(/(access_token|token_hash|token|refresh_token)=([^&\s"']+)/gi, '$1=[REDACTED]')
    .replace(/eyJ[A-Za-z0-9_.-]{20,}/g, '[REDACTED_JWT]');
}

export function SetNewPasswordScreen({ onDone }: { onDone(reason: 'success'|'expired'|'invalid'): void }) {
  // Separate the render-guard from the token itself.
  // `tokenValid` decides whether we render the form vs the invalid-link view;
  // it is set once at mount and NEVER cleared by the request.
  // `captured` holds the actual token; it is cleared immediately after use.
  const [initialised, setInitialised] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const captured = useRef<{ token: string; type: string } | null>(null);

  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [state, setState] = useState<'idle'|'saving'|'ok'|'expired'|'invalid'|'weak'|'mismatch'>('idle');

  useEffect(() => {
    const cap = readRecoveryTokenAndClearUrl();
    captured.current = cap;
    setTokenValid(cap !== null);
    setInitialised(true);
  }, []);

  if (!initialised) return <div role="status" aria-live="polite">Loading…</div>;
  if (!tokenValid) {
    return (
      <div>
        <h1>Recovery link is invalid</h1>
        <p>Please request a new recovery link.</p>
        <button type="button" onClick={() => onDone('invalid')} data-testid="expired-back">
          Request another
        </button>
      </div>
    );
  }
  return (
    <form
      data-testid="set-new-password-form"
      onSubmit={async (e) => {
        e.preventDefault();
        if (pwd !== pwd2) { setState('mismatch'); return; }
        if (!PW_REGEX.test(pwd)) { setState('weak'); return; }
        setState('saving');
        const tokenRef = captured.current;
        if (!tokenRef) {
          // Someone clicked submit after success/replay; render the invalid-link
          // path deterministically instead of pretending success.
          setState('invalid');
          return;
        }
        try {
          const r = await post('/recover/consume', { token: tokenRef.token, new_password: pwd });
          if (r.ok) {
            captured.current = null;
            setState('ok');
            onDone('success');
            return;
          }
          const code = (r.data as { error?: { code?: string } })?.error?.code ?? 'RECOVERY_INVALID';
          // The render guard `tokenValid` stays true so the specific terminal
          // state remains visible even though the token has been cleared.
          setState(code === 'RECOVERY_EXPIRED' ? 'expired' : 'invalid');
          captured.current = null;
        } catch (err) {
          console.error(redactRecovery(err));
          setState('invalid');
          captured.current = null;
        }
      }}
    >
      <h1>Set a new password</h1>
      <p data-testid="pw-rules">{PW_RULES}</p>
      <label>
        New password{' '}
        <input type="password" value={pwd}  onChange={(e) => setPwd(e.target.value)}  autoComplete="new-password" required data-testid="pw-new" />
      </label>
      <label>
        Confirm{' '}
        <input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} autoComplete="new-password" required data-testid="pw-confirm" />
      </label>
      <button type="submit" data-testid="pw-submit" disabled={state === 'saving'}>Save new password</button>
      {state === 'mismatch' && <p role="alert" data-testid="pw-mismatch">Passwords do not match.</p>}
      {state === 'weak'     && <p role="alert" data-testid="pw-weak">Password does not meet requirements.</p>}
      {state === 'expired'  && (
        <p role="alert" data-testid="pw-expired">
          Recovery link has expired. <button type="button" onClick={() => onDone('expired')}>Request another</button>
        </p>
      )}
      {state === 'invalid'  && (
        <p role="alert" data-testid="pw-invalid">
          Recovery link is invalid. <button type="button" onClick={() => onDone('invalid')}>Request another</button>
        </p>
      )}
      {state === 'ok' && <p role="status" aria-live="polite" data-testid="pw-ok">Password updated. Please sign in.</p>}
    </form>
  );
}
